import { isEmpty } from 'lodash-es';
import {
  DiagnosisCodesResponse,
  ReferralUtilityResponseReferralResult,
} from '../../api/optum/types';
import { getNpiIdentifierResponse } from './getNpiByIdentifierResponse';
import {
  LEADING_REACH_REFERRAL_ID_CODE,
  LEADING_REACH_REFERRAL_ID_LABEL,
  LEADING_REACH_STATUS_CODE,
  LEADING_REACH_STATUS_LABEL,
  LEADING_REACH_SYSTEM,
} from '../../utils/leadingReach.consts';

const NOT_RECOMMENDED_CODE = 'NOT_RECOMMENDED';
const OPTUM_FACILITY_NA_NAME = 'N/A';

export const formatReason = (referralReason: string) => {
  return referralReason.replaceAll(/<br \/>/gm, '\n');
};

/**
 * Builds the referral title line. Behind the new-format feature flag the
 * "referral ID" phrase is dropped and the brand is spaced ("Optimal Care").
 */
export const getReferralTitle = (referralId: string, useNewFormat: boolean): string =>
  useNewFormat ? `Optimal Care - ${referralId}` : `OptimalCare - referral ID  ${referralId}`;

export const getFormatResponseToNotes = async (
  response: ReferralUtilityResponseReferralResult,
  shouldUseCuroNotesText: boolean = false,
  shouldUseNewReferralIdFormat: boolean = false,
): Promise<string> => {
  const {
    referralId = '',
    specialist,
    ruSpecialty: specialty,
    clinicalAlgorithm,
    type,
    beginDate,
    endDate,
    numberOfVisits,
    diagnosisCodes,
    referralReason,
    urgency,
    priorAuth,
    provider,
    externalSystemIdentifiers,
  } = response;

  const { identifiers, firstName, lastName, address, phone, fax, clinicLocation, tier } =
    specialist ?? {};

  const referralTitle = getReferralTitle(referralId, shouldUseNewReferralIdFormat);

  const isNameAvailable = firstName !== OPTUM_FACILITY_NA_NAME;
  const targetSpecialist = noteTemplate('Target specialist:', {
    NPI: getNpiIdentifierResponse(identifiers),
    ...(isNameAvailable
      ? { Name: [...(lastName ? [lastName] : []), ...(firstName ? [firstName] : [])].join(' ') }
      : {}),
    'Practice name': clinicLocation,
    Specialty: specialty,
    Address: address,
    Phone: phone,
    Fax: fax,
  });

  const clinicalAlgorithmInformation = getClinicalAlgorithmResponse(clinicalAlgorithm);

  const referralInformation = noteTemplate('Referral information:', {
    Type: type,
    'Start date': beginDate,
    'End date': endDate,
    'Number of visits': numberOfVisits,
    Diagnosis: getDiagnosisCodesResponse(diagnosisCodes),
    Reason: referralReason ? formatReason(referralReason) : undefined,
    Urgency: urgency,
    // Appended for Leading Reach organisations, rendered purely from what the payload carries —
    // no provider-type detection. Market and Tier are ordinary Optum fields that happen to be
    // populated for LR customers; the two below come from the Leading Reach identifiers. Each is
    // omitted when its value is absent.
    //
    // All four are flattened first because they come straight from the payload: a newline or pipe
    // inside one would open a new line in the note, and getOptumReferralIdFromNotes treats a line
    // opening with the brand as a referral title. Flattening keeps that off the reader entirely
    // rather than leaving it to spot a forged title.
    Market: flattenNoteValue(provider?.planName),
    Tier: flattenNoteValue(tier),
    [LEADING_REACH_REFERRAL_ID_LABEL]: flattenNoteValue(
      getLeadingReachValue(externalSystemIdentifiers, LEADING_REACH_REFERRAL_ID_CODE),
    ),
    [LEADING_REACH_STATUS_LABEL]: flattenNoteValue(
      getLeadingReachValue(externalSystemIdentifiers, LEADING_REACH_STATUS_CODE),
    ),
  });

  let priorAuthInformation = '';

  if (priorAuth && shouldUseCuroNotesText) {
    priorAuthInformation = formatPriorAuthBlock({
      priorAuthId: priorAuth.priorAuthId ?? 'null',
      priorAuthStatus: priorAuth.priorAuthStatus,
    });
  }

  return [
    referralTitle,
    '',
    targetSpecialist,
    '',
    referralInformation,
    ...(clinicalAlgorithmInformation ? ['', clinicalAlgorithmInformation] : []),
    ...(priorAuthInformation ? ['', priorAuthInformation] : []),
  ].join('\n');
};

/**
 * Collapses newlines and pipes in a payload-sourced value to single spaces, so it stays on the one
 * note line its label opened. See the call sites for why this matters to the notes reader.
 */
const flattenNoteValue = (value: string | undefined): string | undefined =>
  value?.replaceAll(/\s*[\n\r|]+\s*/g, ' ').trim() || undefined;

export const noteTemplate = (title: string, data: Record<string, string | undefined>) => {
  const note = [title];
  for (const [info, value] of Object.entries(data)) {
    if (value) {
      note.push(`${info}: ${value}`);
    }
  }

  return note.join('\n');
};

export const formatPriorAuthBlock = (fields: Record<string, string | undefined>): string =>
  noteTemplate('Prior authorization information:', fields);

/** Presence check for observability — same match rule as the rendered field, no value exposed. */
export const hasLeadingReachValue = (
  externalSystemIdentifiers: ReferralUtilityResponseReferralResult['externalSystemIdentifiers'],
  code: string,
): boolean => !!getLeadingReachValue(externalSystemIdentifiers, code);

const getLeadingReachValue = (
  externalSystemIdentifiers: ReferralUtilityResponseReferralResult['externalSystemIdentifiers'],
  code: string,
): string | undefined =>
  externalSystemIdentifiers?.find((i) => i.system === LEADING_REACH_SYSTEM && i.code === code)
    ?.value;

const getDiagnosisCodesResponse = (diagnosisCodes: DiagnosisCodesResponse[] | undefined) => {
  if (!diagnosisCodes) return;
  return [
    '',
    ...diagnosisCodes.map(({ value, description }) => {
      return ` code: ${value} description: ${description}`;
    }),
  ].join('\n');
};

const getClinicalAlgorithmResponse = (
  clinicalAlgorithm: ReferralUtilityResponseReferralResult['clinicalAlgorithm'],
) => {
  const {
    clinicalAlgorithmResponses,
    code,
    reasonAlgorithmIgnored,
    reasonCommentAlgorithmIgnored,
  } = clinicalAlgorithm ?? {};

  const prefixText = !isEmpty(clinicalAlgorithmResponses) ? 'Clinical algorithm:' : undefined;

  const clinicalAlgorithmResult = clinicalAlgorithmResponses
    ?.map(
      ({ question, response }) =>
        ` Question: ${question}${response ? `\n Response: ${response}` : ''}`,
    )
    .join('\n');

  const notRecommendedReason =
    code === NOT_RECOMMENDED_CODE
      ? `\nReferral not recommended\nProvider's reason to proceed with the referral: ${
          reasonAlgorithmIgnored ?? 'N/A'
        }\nAdditional comments : ${reasonCommentAlgorithmIgnored ?? 'N/A'}`
      : undefined;

  return [prefixText, clinicalAlgorithmResult, notRecommendedReason].filter(Boolean).join('\n');
};
